/** Tracks overview — table of all tracks from all sources with search and pagination */
import { useState, useEffect, useCallback } from 'react';
import { rpc } from '@/api/client';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { Pagination } from '@/components/common/Pagination';
import { FilterBar } from '@shared/search';
import { tracksSchema } from './tracksSchema';
import { MapPin, Clock, Trophy, Timer } from 'lucide-react';
import { formatTime } from '@/lib/format';
import type { ListTracksResponse, TrackSummary } from '@/types/telemetry';

const PAGE_SIZE = 24;

export function TracksOverview() {
  const { params, navigate } = useRouter();
  const { t } = useI18n();

  const page = parseInt(params.page || '1', 10);
  const query = params.q || '';

  const [data, setData] = useState<ListTracksResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (currentPage: number, currentQuery: string) => {
    setLoading(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { page: currentPage, pageSize: PAGE_SIZE };
      if (currentQuery) body.query = currentQuery;
      const res = await rpc<ListTracksResponse>('ListTracks', body);
      setData(res);
    } catch (e: any) {
      setError(e.message || t('tracks.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadData(page, query); }, [page, query, loadData]);

  const buildHash = (p: number, q: string) => {
    const qp = new URLSearchParams();
    qp.set('page', String(p));
    if (q) qp.set('q', q);
    return `#/tracks?${qp.toString()}`;
  };

  const handlePageChange = useCallback((newPage: number) => {
    navigate(buildHash(newPage, query));
  }, [navigate, query]);

  const tracks = data?.tracks || [];
  const total = data?.totalCount || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center">
          <MapPin size={18} className="text-accent" />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('tracks.title')}</h1>
          <p className="text-xs text-text-muted mt-0.5">{t('tracks.subtitle')}</p>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="mb-6">
        <FilterBar
          schema={tracksSchema}
          initialQuery={query}
          onSearch={(q) => navigate(buildHash(1, q))}
          enableFilterUI={true}
        />
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorState message={error} />
      ) : tracks.length === 0 ? (
        <ErrorState message={t('tracks.noData')} isNotFound />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {tracks.map((track) => (
              <TrackCard key={track.id} track={track} />
            ))}
          </div>

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </div>
  );
}

function TrackCard({ track }: { track: TrackSummary }) {
  const { navigate } = useRouter();
  const { t } = useI18n();
  const [imgError, setImgError] = useState(false);

  const showImage = !imgError && (!!track.hasImage || (track.latitude !== 0 && track.longitude !== 0));

  return (
    <div
      onClick={() => navigate(`#/tracks/${track.source || 'canonical'}/${encodeURIComponent(track.id)}`)}
      className="card p-0 overflow-hidden hover:border-border-hover transition-all cursor-pointer flex flex-col justify-between group"
    >
      <div>
        {/* Satellite Image Banner */}
        <div className="relative w-full aspect-[16/9] bg-bg-surface border-b border-border-subtle overflow-hidden flex items-center justify-center">
          {showImage ? (
            <img
              src={`/rpc/GetTrackImage?trackId=${encodeURIComponent(track.id)}`}
              alt={track.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-1.5 text-text-muted">
              <div className="w-9 h-9 rounded-full bg-bg-base border border-border-subtle flex items-center justify-center">
                <MapPin size={16} className="text-text-muted" />
              </div>
              <span className="text-[0.68rem] font-medium tracking-wide">
                {track.latitude && track.longitude
                  ? `${track.latitude.toFixed(4)}, ${track.longitude.toFixed(4)}`
                  : t('tracks.sourceCanonical')}
              </span>
            </div>
          )}
          <span className="absolute top-2.5 right-2.5 text-xs font-mono text-text-secondary px-2 py-0.5 rounded bg-bg-base/80 backdrop-blur-sm border border-border-subtle shrink-0">
            {track.source === 'canonical'
              ? t('tracks.sourceCanonical')
              : track.source === 'session'
              ? t('tracks.sourceSession')
              : t('tracks.sourceRaceEvent')}
          </span>
        </div>

        {/* Card Body */}
        <div className="p-4">
          <h3 className="font-semibold text-text-primary text-base leading-snug group-hover:text-accent transition-colors">
            {track.name}
          </h3>
        </div>
      </div>

      <div className="p-4 pt-0">
        <div className="pt-3 border-t border-border-subtle grid grid-cols-3 gap-2 text-xs">
          <div>
            <span className="text-text-muted block text-[0.68rem] flex items-center gap-1">
              <Clock size={11} />
              {t('tracks.sessionsCount')}
            </span>
            <span className="font-mono font-medium text-text-secondary mt-0.5 block">
              {(track.sessionCount ?? 0).toLocaleString()}
            </span>
          </div>
          <div>
            <span className="text-text-muted block text-[0.68rem] flex items-center gap-1">
              <Trophy size={11} />
              {t('tracks.raceTab')}
            </span>
            <span className="font-mono font-medium text-text-secondary mt-0.5 block">
              {(track.raceSessionCount ?? 0).toLocaleString()}
            </span>
          </div>
          <div>
            <span className="text-text-muted block text-[0.68rem] flex items-center gap-1">
              <Timer size={11} />
              {t('tracks.bestLap')}
            </span>
            <span className="font-mono font-medium text-text-secondary mt-0.5 block truncate">
              {track.bestLapTimeMs ? formatTime(track.bestLapTimeMs) : '—'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

